import { PureComponent } from "react";
import * as React from "react";
import _ from 'lodash';

import { PartialIWidgetThingsProps, IconURIPath, WidgetLocalizationKeys } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';

import { isDefined, getBool, IsNullOrEmpty } from '@Utils/Micro';

import { WebThingPort, WebThing } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType, StateStatus } from '@API/WebNodeTypes';

import { SettingsList } from '@Primitive/SettingsList';
import { SettingsType } from '@Primitive/SettingsListType';

import { LocalizationMap } from '@Primitive/Localization/Localization';

import './Led.less';
// =======================================================================================================
interface LedData {
    state: boolean;
    color: string;
}

enum LedShape {
    Bulb,
    Dot
}

enum LedSizePolicy {
    Auto,
    Static
}

interface LedSize {
    policy: LedSizePolicy;
    pixels: number;
}

export interface LedSettings {
    shape: LedShape;
    size: LedSize;
    colorWhenOn: string;
    colorWhenOff: string;
    animation: boolean;
}
// ------------------------------------------------------------------------------------------------------
interface LedState {
}
class Led extends React.Component<PartialIWidgetThingsProps<LedSettings>, LedState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<LedSettings>) {
        super(props);
        this.state = {};
    }
    // ---------------------------------------------------------------------------------------------------
    getStateFromProps(props: PartialIWidgetThingsProps<LedSettings>) {
        let state = {
            color: undefined,
            isOn: false
        };

        // try parse 'data'
        if (_.has(props.PortsStates, 'data')) {
            let port = props.PortsStates['data'];
            if (port.LastStateStatus == StateStatus.Valid) {
                const data: LedData = port.State;
                state.isOn = getBool(data.state);
                state.color = data.color;
            }
        }

        return state;
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const { width, height, WidgetData } = this.props;
        let state = this.getStateFromProps(this.props);

        let ledWidth = width;
        let ledHeight = height;

        // calculate the dimension
        if (WidgetData.size.policy == LedSizePolicy.Static) {
            ledWidth = WidgetData.size.pixels;
            ledHeight = WidgetData.size.pixels;

            if (ledWidth > width || ledHeight > height) {
                const minSize = _.min([width, height]);
                ledWidth = minSize;
                ledHeight = minSize;
            }
        }

        // set the color of the led
        let color = null;
        if (IsNullOrEmpty(state.color)) {
            color = state.isOn ? WidgetData.colorWhenOn : WidgetData.colorWhenOff;
        } else {
            color = state.color;
        }

        let className = null;
        if (WidgetData.animation) {
            className = 'led-animation';
        }

        // create element from shape 
        let jsx = null;
        switch (WidgetData.shape) {
            case LedShape.Bulb:
                jsx = <svg
                    viewBox='0 0 64 64'
                    width={ledWidth}
                    height={ledHeight}
                    style={{
                        display: 'block',
                        margin: '0 auto'
                    }}>
                    <path
                        className={className}
                        fill={color}
                        stroke='black'
                        strokeWidth='1'
                        d='M 24.98,36.09 C 22.99,32.34 15.25,28.65 16.20,19.00 18.17,-1.02 46.81,-0.33 47.89,19.00 48.48,29.44 41.16,32.29 39.02,36.17 37.79,38.41 38.01,41.50 38.00,44.00 38.00,44.00 26.00,44.00 26.00,44.00 25.99,41.45 26.20,38.40 24.98,36.09 Z' />
                    <path
                        fill='black'
                        stroke='black'
                        strokeWidth='1'
                        d='M 51.67,23.00 C 50.33,30.87 45.33,34.42 43.56,37.17 41.29,40.69 42.54,43.35 40.98,45.69 38.99,48.66 33.21,48.05 30.00,48.00 19.11,47.80 24.17,43.08 20.41,37.00 18.46,33.86 14.62,31.37 12.79,25.00 9.41,13.26 17.88,3.12 29.00,0.47 42.12,-1.24 53.97,9.57 51.67,23.00 Z M 39.02,36.17 C 41.16,32.29 48.48,29.44 47.89,19.00 46.94,1.96 23.74,-1.51 17.23,14.00 12.13,26.13 23.17,32.89 24.98,36.17 26.21,38.41 25.99,41.50 26.00,44.00 26.00,44.00 38.00,44.00 38.00,44.00 38.01,41.50 37.79,38.41 39.02,36.17 Z' />
                    <path
                        fill='black'
                        stroke='black'
                        strokeWidth='1'
                        d='M 40.00,51.00 C 40.00,51.00 40.00,53.00 40.00,53.00 34.86,54.46 29.14,54.46 24.00,53.00 24.00,53.00 24.00,51.00 24.00,51.00 29.14,49.54 34.86,49.54 40.00,51.00 Z' />
                    <path
                        fill='black'
                        stroke='black'
                        strokeWidth='1'
                        d='M 40.00,57.00 C 36.25,66.46 27.75,66.46 24.00,57.00 29.14,55.54 34.86,55.54 40.00,57.00 Z' />
                </svg>;
                break;

            case LedShape.Dot:
                const dotSize = _.min([ledWidth, ledHeight]);
                jsx = <div
                    className={className}
                    style={{
                        height: dotSize,
                        width: dotSize,
                        backgroundColor: color,
                        borderRadius: '50%',
                        margin: '0 auto'
                    }} />;
                break;

            default:
                break;
        }

        return <div style={{
            width: width,
            height: height,
            overflow: 'hidden'
        }}>
            {jsx}
        </div>;
    }
}

// ------------------------------------------------------------------------------------------------------
class LedWidgetSettings extends PureComponent<IWidgetSettingsProps<LedSettings>> {
    constructor(props: IWidgetSettingsProps<LedSettings>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        return <SettingsList
            AutoSave
            SettingsMap={[
                {
                    name: '',
                    elements: [
                        {
                            type: SettingsType.DropDown,
                            dataPath: 'shape',
                            label: 'Shape',
                            tooltip: 'The external form of the led',
                            items: [
                                {
                                    label: 'Bulb',
                                    value: LedShape.Bulb
                                },
                                {
                                    label: 'Dot',
                                    value: LedShape.Dot
                                }
                            ]
                        },
                        {
                            type: SettingsType.DropDown,
                            dataPath: 'size.policy',
                            label: 'Size Policy',
                            tooltip: 'How the width and height will be scaled',
                            items: [
                                {
                                    label: 'Auto',
                                    value: LedSizePolicy.Auto
                                },
                                {
                                    label: 'Static',
                                    value: LedSizePolicy.Static
                                }
                            ]
                        },
                        {
                            type: SettingsType.Slider,
                            dataPath: 'size.pixels',
                            label: 'Size',
                            tooltip: 'The width and height in pixels',
                            minValue: 4,
                            maxValue: 200,
                            stepValue: 1,
                            valuePosfix: 'px',
                            isHidden: d => d.size.policy != LedSizePolicy.Static
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: 'Active Color',
                            dataPath: 'colorWhenOn',
                            tooltip: 'The color when it becomes active (state is true)'
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: 'Inactive Color',
                            dataPath: 'colorWhenOff',
                            tooltip: 'The color when it becomes inactive (state is false)'
                        },
                        {
                            type: SettingsType.Boolean,
                            dataPath: 'animation',
                            label: 'Blinking Animation',
                            tooltip: 'Gradually changes the opacity to create a blink effect'
                        }
                    ]
                }
            ]}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// Button Description interface
const enum PortKey {
    Data = 'data'
}
export class LedWidget extends IWidgetThingsClass<LedSettings> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <Led />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <LedWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    private getPorts(): Plegma.Port[] {
        return [
            {
                Name: "Data",
                PortKey: PortKey.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: "{}"
            }
        ];
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};
        this.getPorts().forEach(element => {
            thing.AddPort(new WebThingPort(element, WebThingPortType.Data), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        this.getPorts().forEach(element => {
            ports.push(new WebThingPort(element, WebThingPortType.Data));
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): LedSettings {
        return {
            shape: LedShape.Bulb,
            size: {
                policy: LedSizePolicy.Auto,
                pixels: 4
            },
            colorWhenOn: "#FFEB3B",
            colorWhenOff: "white",
            animation: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return LedWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: LedSettings = LedWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Led",
            WidgetTitle: "",
            Key: "LedWidget",
            Type: "LedWidget",
            Data: data,
            Description: "Led Widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            DefiesGrid: true,
            WidgetNoBackground: true,
            IconURI: IconURIPath + "/svg/led.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string {
        return PortKey.Data;
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================